import {
  CLOCK_DRIFT_FLAG_MS,
  MAX_PLAUSIBLE_EPOCH_MS,
  MIN_PLAUSIBLE_EPOCH_MS,
} from './constants.js';

/**
 * Clock discipline (optisigns-parity §3, design-addendum §11).
 *
 * A cheap stick can boot with no NTP and an implausible clock. Every timestamp the
 * player emits is UTC and is device time plus an offset learned from the first
 * server response's `Date` header; the offset is reported in the heartbeat and
 * flagged past five minutes of drift.
 */
export interface TimeSource {
  /** Raw device clock, unadjusted. Used to measure durations. */
  deviceNowMs(): number;
  /** Device clock plus the learned server offset. Used for every emitted timestamp. */
  nowMs(): number;
  nowIso(): string;
  isoFromDeviceMs(deviceMs: number): string;
  /** True until a server response has corrected an implausible device clock. */
  isUnsynced(): boolean;
  offsetMs(): number;
  driftExceedsThreshold(): boolean;
  /** Called with the server's `Date` header on every response that carries one. */
  observeServerTime(serverMs: number): void;
}

export interface TimeSourceOptions {
  readonly deviceClock: () => number;
  /** Offset restored from disk across a reboot, if any. */
  readonly initialOffsetMs?: number;
}

export function createTimeSource(options: TimeSourceOptions): TimeSource {
  let offsetMs = options.initialOffsetMs ?? 0;
  let synced = false;

  const plausible = (): boolean => {
    const now = options.deviceClock() + offsetMs;
    return now >= MIN_PLAUSIBLE_EPOCH_MS && now <= MAX_PLAUSIBLE_EPOCH_MS;
  };

  return {
    deviceNowMs: () => options.deviceClock(),
    nowMs: () => options.deviceClock() + offsetMs,
    nowIso: () => new Date(options.deviceClock() + offsetMs).toISOString(),
    isoFromDeviceMs: (deviceMs) => new Date(deviceMs + offsetMs).toISOString(),
    isUnsynced: () => !synced && !plausible(),
    offsetMs: () => offsetMs,
    driftExceedsThreshold: () => Math.abs(offsetMs) > CLOCK_DRIFT_FLAG_MS,
    observeServerTime: (serverMs) => {
      offsetMs = serverMs - options.deviceClock();
      synced = true;
    },
  };
}

export function isPlausibleEpochMs(ms: number): boolean {
  return ms >= MIN_PLAUSIBLE_EPOCH_MS && ms <= MAX_PLAUSIBLE_EPOCH_MS;
}
